import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import QueryParamsModel from '../../../../../services/queryParams.model';
import {QueryParamsService} from '../../../../../services/queryParams.service';
import {TestCategoryService} from '../../../../../services/testCategory.service';
import TestCategoryModel from '../../../../../services/testCategory.model';
import QPTypeModel from '../../../../../services/qpType.model';
import {QPTypeService} from '../../../../../services/qpType.service';
import * as moment from 'moment';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare let $: any;
/**
 * QueryParamsComponent
 *
 * This component is responsible for managing Quality Parameters (QP) within the Test Configurations module.
 * It provides a user interface for administrators to view, create, update, and search for quality parameters.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all quality parameters.
 * - Provide search functionality to filter parameters by name.
 * - Manage the creation of new quality parameters with validation.
 * - Handle updates to existing quality parameters.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update quality parameters.
 * - **Real-time Search**: Filter list by parameter name.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures parameter names follow specific patterns (alphanumeric, spaces).
 * - **Date Formatting**: Formats 'updatedAt' timestamps for display.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches all quality parameters from `QueryParamsService`.
 * 3. Fetches reference data (Test Categories, QP Types) for dropdowns.
 * 4. Formats dates using `moment`.
 * 5. Initializes `BatchLoader` with the full dataset.
 * 6. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 7. `addUpdateQueryParams` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-query-params
 * @example
 * <app-query-params></app-query-params>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-query-params',
  templateUrl: './query-params.component.html',
  styleUrls: ['./query-params.component.scss']
})
export class QueryParamsComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched quality parameters from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {QueryParamsModel[]}
   * @default []
   */
  public queryParams: QueryParamsModel[] = [];

  /**
   * Array holding the currently displayed quality parameters.
   * This list is populated by the BatchLoader (subset of queryParams or filteredQueryParam).
   * @type {QueryParamsModel[]}
   * @default []
   */
  public queryParamsList: QueryParamsModel[] = [];

  /**
   * Model for the current quality parameter being added or edited.
   * Bound to the form for data manipulation.
   * @type {QueryParamsModel}
   */
  public queryParam: QueryParamsModel = new QueryParamsModel();

  /**
   * Array holding quality parameters that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {QueryParamsModel[]}
   * @default []
   */
  public filteredQueryParam: QueryParamsModel[] = [];

  /**
   * Array of available Test Categories for selection.
   * @type {Array<TestCategoryModel>}
   */
  public testCategories: Array<TestCategoryModel>;

  /**
   * Selected Test Category model.
   * @type {TestCategoryModel}
   */
  public testCategory: TestCategoryModel;

  /**
   * Array of available QP Types for selection.
   * @type {Array<QPTypeModel>}
   */
  public qpTypes: Array<QPTypeModel>;

  /**
   * Selected QP Type model.
   * @type {QPTypeModel}
   */
  public qpType: QPTypeModel;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Flag to disable UI elements (not actively used in current logic).
   * @type {boolean}
   * @default true
   */
  disabled = true;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag to prevent double clicks on submit actions.
   * @type {boolean}
   */
  public queryParamsDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   * @default false
   */
  public clearSearchInput: boolean = false;

  /**
   * Reactive form group for quality parameter details.
   * @type {FormGroup}
   */
  public registerForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   * @default false
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Timeout reference for search input debouncing.
   * @type {any}
   */
  searchTimeout: any;

  /**
   * Count of fields in the form, used for layout adjustments.
   * @type {number}
   * @default 0
   */
  public formFieldCount: number = 0;

  /**
   * Flag to toggle the visibility of the add/edit form.
   * @type {boolean}
   * @default false
   */
  public showForm: boolean = false;

  /**
   * Constructor for QueryParamsComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - name: Required, Min 3, Max 20, Pattern (alphabetic, spaces).
   * - abbreviation: Required, Max 10, Pattern (alphanumeric).
   * - description: Required, Max 200 characters.
   * - uom: Required, Min 1, Max 10.
   * - unit_code: Required, Min 3, Max 10, Pattern (alphanumeric).
   * - qp_type_id: Required.
   * - test_category_id: Required.
   *
   * @constructor
   * @param {ToastrService} toastrService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {QueryParamsService} queryParamsService - Service for Query Params API operations.
   * @service QueryParamsService
   * @param {TestCategoryService} testCategoryService - Service for Test Category API operations.
   * @service TestCategoryService
   * @param {QPTypeService} qpTypeService - Service for QP Type API operations.
   * @service QPTypeService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public sidenavaction: Sidenavaction, public queryParamsService: QueryParamsService, public testCategoryService: TestCategoryService,
              public qpTypeService: QPTypeService, public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService) {
    this.queryParam = new QueryParamsModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.required, Validators.maxLength(200)]],
      uom: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(10)]],
      unit_code: ['', [Validators.required, Validators.minLength(3) ,Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      qp_type_id : ['', [Validators.required]],
      test_category_id : ['', [Validators.required]],
      is_derived: [null],
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Sets up side navigation.
   * 2. Resets component state and form.
   * 3. Tracks the current route.
   * 4. Fetches reference data (Test Categories, QP Types).
   * 5. Fetches all quality parameters from the backend.
   * 6. Formats the 'updatedAt' date for each parameter.
   * 7. Initializes the batch loader and loads the first batch of data.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    // this.pgLoad = false;
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.queryParams = [];
    this.queryParamsList = [];
    this.filteredQueryParam = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.testCategories = await this.testCategoryService.getAllTestCategory();
      this.qpTypes = await this.qpTypeService.getAllQpTypes();
      await this.getQueryParams();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
    this.queryParam.qp_type_id = undefined;
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method f
   * @returns {AbstractControl} Form controls
   */
  get f() {
    return this.registerForm.controls;
  }

  /**
   * Fetches all quality parameters from the backend.
   * Formats dates and initializes the batch loader.
   *
   * @async
   * @method getQueryParams
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async getQueryParams() {
    try {
      this.queryParams = await this.queryParamsService.getAllQueryParam();
      if (this.queryParams.length > 0) {
        this.queryParams.forEach(eachQueryParam => {
          eachQueryParam.updatedAt = JSON.parse(JSON.stringify(moment(eachQueryParam.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
          this.qpType = this.getQPTypeById(JSON.stringify(eachQueryParam.qp_type_id));
          this.queryParams = JSON.parse(JSON.stringify(this.queryParams));
          eachQueryParam.qpType = this.qpType;
          this.testCategory = this.getTestCategoryById(JSON.stringify(eachQueryParam.test_category_id));
          eachQueryParam.testCategory = this.testCategory;
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 100);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredQueryParam : this.queryParams,
      (updatedList) => {
        this.queryParamsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for all injected services.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.testCategoryService.cancelOnGoingHttpRequests();
    this.qpTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /**
   * Filters the quality parameter list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredQueryParam`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredQueryParam`, `queryParamsList`, `noList`, `isFiltering`.
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.queryParams.filter(qp =>
        qp.name?.toLowerCase().includes(query)
      )
      : this.queryParams;
    this.filteredQueryParam = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.queryParamsList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * Debounces search input changes.
   * Calls `applySearch` after a delay to prevent excessive filtering.
   *
   * @method onSearchChanged
   * @returns {void}
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * Resets the form and component state to default values.
   *
   * @method clear
   * @returns {void}
   */
  public clear() {
    this.showForm = false;
    this.registerForm.reset();
    this.queryParam = new QueryParamsModel();
    this.submitButton = false;
    this.updateButton = false;
    this.queryParamsDoubleClick = false;
    this.submitted = false;
  }

  /**
   * Handles the submission of the Quality Parameter form (Add or Update).
   *
   * - Validates the form.
   * - Calls `create` or `updateOneByID` service methods based on mode.
   * - Displays success notification.
   * - Refreshes the list via `ngOnInit`.
   *
   * @async
   * @method addUpdateQueryParams
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async addUpdateQueryParams() {
    this.disabled = false;
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.queryParamsDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      formData.created_name = localStorage.getItem('LoginUserName');
      formData.updated_name = localStorage.getItem('LoginUserName');
      if (this.submitButton) {
        await this.queryParamsService.create(formData);
        this.toastrService.success('Quality parameter created successfully', '', {
          timeOut: 3000
        });
      } else {
        const updateQueryParam = {...formData}
        delete updateQueryParam.id;
        await this.queryParamsService.updateOneByID(this.queryParam.id, updateQueryParam);
        this.toastrService.success('Quality parameter updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.queryParamsDoubleClick = false;
    }
  }

  /**
   * Retrieves a Quality Parameter by ID and populates the form for editing.
   * Sets the component to 'Update' mode.
   *
   * @method getQueryParamsById
   * @param {string} id - The ID of the quality parameter to edit.
   * @returns {Promise<void>}
   */
  public async getQueryParamsById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.queryParamsDoubleClick = false;
    const foundQueryParams = this.queryParamsList.find(uom => uom?.id === id);
    this.queryParam = foundQueryParams ? { ...foundQueryParams } : new QueryParamsModel(); // clone object
    if (this.queryParam) {
      this.registerForm.patchValue({
        name: this.queryParam.name,
        abbreviation: this.queryParam.abbreviation,
        description: this.queryParam.description,
        uom: this.queryParam.uom,
        unit_code: this.queryParam.unit_code,
        qp_type_id: this.queryParam.qp_type_id,
        test_category_id: this.queryParam.test_category_id,
        is_derived: this.queryParam.is_derived,
      });
    }
  }

  /**
   * Retrieves a QP Type by ID from the local list.
   *
   * @method getQPTypeById
   * @param {string} id - The ID of the QP Type.
   * @returns {QPTypeModel} The found QP Type model.
   */
  public getQPTypeById(id: string) {
    this.qpType = this.qpTypes.find(qpType => qpType && qpType.id == id);
    return this.qpType;
  }

  /**
   * Retrieves a Test Category by ID from the local list.
   *
   * @method getTestCategoryById
   * @param {string} id - The ID of the Test Category.
   * @returns {TestCategoryModel} The found Test Category model.
   */
  public getTestCategoryById(id: string) {
    this.testCategory = this.testCategories.find(testCategory => testCategory && testCategory.id == id);
    return this.testCategory;
  }

  /**
   * Opens the form in 'Create' mode.
   *
   * @method openForm
   * @returns {void}
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
